import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Declarar tipo global para gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    fbq: (...args: any[]) => void;
  }
}

interface ExternalScript {
  id: string;
  name: string;
  script_type: 'GA' | 'GTM' | 'META_PIXEL' | 'OTHER';
  tracking_id: string;
  is_active: boolean;
  deleted_at: string | null;
}

// Função para injetar Google Analytics 4
const injectGA4Script = (trackingId: string) => {
  // Verificar se já existe
  if (document.querySelector(`script[src*="${trackingId}"]`)) {
    console.log(`✅ GA4 ${trackingId} já está carregado`);
    return;
  }

  // Criar script do gtag.js
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
  document.head.appendChild(script);

  // Criar script de configuração
  const configScript = document.createElement('script');
  configScript.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${trackingId}');
  `;
  document.head.appendChild(configScript);

  console.log(`✅ Google Analytics ${trackingId} carregado com sucesso!`);
};

// Função para injetar Google Tag Manager
const injectGTMScript = (trackingId: string) => {
  // Verificar se já existe
  if (document.querySelector(`script[src*="${trackingId}"]`)) {
    console.log(`✅ GTM ${trackingId} já está carregado`);
    return;
  }

  // Script do GTM no head
  const headScript = document.createElement('script');
  headScript.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${trackingId}');
  `;
  document.head.appendChild(headScript);

  // Noscript do GTM no body
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${trackingId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);

  console.log(`✅ Google Tag Manager ${trackingId} carregado com sucesso!`);
};

// Função para injetar Meta Pixel
const injectMetaPixelScript = (trackingId: string) => {
  // Verificar se já existe
  if (document.querySelector(`script[data-pixel-id="${trackingId}"]`)) {
    console.log(`✅ Meta Pixel ${trackingId} já está carregado`);
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('data-pixel-id', trackingId);
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${trackingId}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(script);

  // Adicionar noscript
  const noscript = document.createElement('noscript');
  const img = document.createElement('img');
  img.height = 1;
  img.width = 1;
  img.style.display = 'none';
  img.src = `https://www.facebook.com/tr?id=${trackingId}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.head.appendChild(noscript);

  console.log(`✅ Meta Pixel ${trackingId} carregado com sucesso!`);
};

// Função para remover script
const removeScript = (scriptType: string, trackingId: string) => {
  let selector = '';
  
  if (scriptType === 'GA') {
    selector = `script[src*="${trackingId}"]`;
  } else if (scriptType === 'GTM') {
    selector = `script[src*="${trackingId}"]`;
  } else if (scriptType === 'META_PIXEL') {
    selector = `script[data-pixel-id="${trackingId}"]`;
  }

  const elements = document.querySelectorAll(selector);
  elements.forEach(el => el.remove());
  
  console.log(`🗑️ Script ${scriptType} ${trackingId} removido`);
};

export const useExternalScripts = () => {
  useEffect(() => {
    const loadScripts = async () => {
      try {
        // Buscar scripts ativos do banco de dados
        const { data: scripts, error } = await supabase
          .from('external_scripts')
          .select('*')
          .eq('is_active', true)
          .is('deleted_at', null);

        if (error) {
          console.error('❌ Erro ao carregar scripts:', error);
          return;
        }

        console.log(`📊 ${scripts?.length || 0} scripts ativos encontrados`);

        // Injetar cada script ativo
        scripts?.forEach((script) => {
          if (script.script_type === 'GA') {
            injectGA4Script(script.tracking_id);
          } else if (script.script_type === 'GTM') {
            injectGTMScript(script.tracking_id);
          } else if (script.script_type === 'META_PIXEL') {
            injectMetaPixelScript(script.tracking_id);
          }
        });
      } catch (err) {
        console.error('❌ Erro ao carregar scripts:', err);
      }
    };

    loadScripts();

    // Configurar listener para mudanças em tempo real
    const channel = supabase
      .channel('external-scripts-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'external_scripts',
        },
        (payload) => {
          console.log('🔄 Mudança detectada nos scripts:', payload);
          
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const script = payload.new as ExternalScript;
            
            if (script.is_active && !script.deleted_at) {
              // Adicionar novo script
              if (script.script_type === 'GA') {
                injectGA4Script(script.tracking_id);
              } else if (script.script_type === 'GTM') {
                injectGTMScript(script.tracking_id);
              } else if (script.script_type === 'META_PIXEL') {
                injectMetaPixelScript(script.tracking_id);
              }
            } else {
              // Remover script desativado
              removeScript(script.script_type, script.tracking_id);
            }
          } else if (payload.eventType === 'DELETE') {
            const script = payload.old as ExternalScript;
            removeScript(script.script_type, script.tracking_id);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);
};
